import React, { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { HelmetProvider } from "react-helmet-async";

import { AuthProvider } from "./lib/auth";
import { ContentProvider } from "./lib/content";
import { SiteProvider } from "./lib/site";
import PublicLayout from "./components/public/PublicLayout";

import Home from "./pages/public/Home";
import Services from "./pages/public/Services";
import ServiceDetail from "./pages/public/ServiceDetail";
import ServiceLocation from "./pages/public/ServiceLocation";
import Locations from "./pages/public/Locations";
import LocationDetail from "./pages/public/LocationDetail";
import Gallery from "./pages/public/Gallery";
import Blog from "./pages/public/Blog";
import BlogPost from "./pages/public/BlogPost";
import Contact from "./pages/public/Contact";

import AdminLogin from "./pages/admin/AdminLogin";
import AuthCallback from "./pages/admin/AuthCallback";
import AdminLayout from "./pages/admin/AdminLayout";
import Dashboard from "./pages/admin/Dashboard";
import AdminServices from "./pages/admin/AdminServices";
import AdminGallery from "./pages/admin/AdminGallery";
import AdminBlog from "./pages/admin/AdminBlog";
import AdminBeforeAfter from "./pages/admin/AdminBeforeAfter";
import AdminTestimonials from "./pages/admin/AdminTestimonials";
import AdminContacts from "./pages/admin/AdminContacts";
import AdminContent from "./pages/admin/AdminContent";
import AdminUsers from "./pages/admin/AdminUsers";
import AdminSettings from "./pages/admin/AdminSettings";

function withPublic(El) {
  return (
    <PublicLayout>
      <El />
    </PublicLayout>
  );
}

function AppRouter() {
  const location = useLocation();
  // Synchronously detect OAuth callback
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      {/* Public */}
      <Route path="/" element={withPublic(Home)} />
      <Route path="/services" element={withPublic(Services)} />
      <Route path="/services/:slug/:location" element={withPublic(ServiceLocation)} />
      <Route path="/services/:slug" element={withPublic(ServiceDetail)} />
      <Route path="/locations" element={withPublic(Locations)} />
      <Route path="/locations/:slug" element={withPublic(LocationDetail)} />
      <Route path="/gallery" element={withPublic(Gallery)} />
      <Route path="/blog" element={withPublic(Blog)} />
      <Route path="/blog/:slug" element={withPublic(BlogPost)} />
      <Route path="/contact" element={withPublic(Contact)} />

      {/* Admin login */}
      <Route path="/admin" element={<AdminLogin />} />

      {/* Protected admin */}
      <Route element={<AdminLayout />}>
        <Route path="/admin/dashboard" element={<Dashboard />} />
        <Route path="/admin/services" element={<AdminServices />} />
        <Route path="/admin/gallery" element={<AdminGallery />} />
        <Route path="/admin/blog" element={<AdminBlog />} />
        <Route path="/admin/before-after" element={<AdminBeforeAfter />} />
        <Route path="/admin/testimonials" element={<AdminTestimonials />} />
        <Route path="/admin/contacts" element={<AdminContacts />} />
        <Route path="/admin/content" element={<AdminContent />} />
        <Route path="/admin/users" element={<AdminUsers />} />
        <Route path="/admin/settings" element={<AdminSettings />} />
      </Route>

      <Route path="*" element={withPublic(Home)} />
    </Routes>
  );
}

function App() {
  useEffect(() => {
    // Remove Emergent badge from all pages (public + admin)
    const removeBadge = () => {
      const el = document.getElementById("emergent-badge");
      if (el) el.remove();
    };
    removeBadge();
    const obs = new MutationObserver(removeBadge);
    obs.observe(document.body, { childList: true, subtree: false });
    const t = setTimeout(() => obs.disconnect(), 5000);
    return () => { obs.disconnect(); clearTimeout(t); };
  }, []);

  return (
    <div className="App">
      <BrowserRouter>
        <HelmetProvider>
          <AuthProvider>
            <ContentProvider>
              <SiteProvider>
                <AppRouter />
                <Toaster richColors position="top-right" theme="dark" />
              </SiteProvider>
            </ContentProvider>
          </AuthProvider>
        </HelmetProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
